/**
 * Utilities for capturing gallery presentation cards via html2canvas.
 * Handles video element replacement (cross-origin videos can't be drawn to canvas)
 * and scale/overflow restoration for accurate full-resolution captures.
 */

type VideoReplacement = { video: HTMLVideoElement; img: HTMLImageElement };

/**
 * Generate a styled poster image (data URL) for a video element.
 * Used as a placeholder when cross-origin videos can't be canvas-captured.
 */
export function buildVideoPoster(width: number, height: number, label?: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = width || 480;
  canvas.height = height || 270;
  const ctx = canvas.getContext('2d')!;

  // Dark gradient background
  const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
  grad.addColorStop(0, '#1a1a2e');
  grad.addColorStop(1, '#16213e');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Play button triangle
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const sz = Math.min(canvas.width, canvas.height) * 0.15;
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.beginPath();
  ctx.moveTo(cx - sz * 0.4, cy - sz * 0.5);
  ctx.lineTo(cx + sz * 0.6, cy);
  ctx.lineTo(cx - sz * 0.4, cy + sz * 0.5);
  ctx.closePath();
  ctx.fill();

  if (label) {
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = `${Math.max(12, Math.round(canvas.width / 30))}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(label, cx, cy + sz + 20);
  }

  return canvas.toDataURL('image/png');
}

/**
 * Replace all <video> elements inside an element with poster <img> tags.
 * Returns replacement pairs for later restoration.
 */
export function replaceVideosWithFrames(el: HTMLElement): VideoReplacement[] {
  const replacements: VideoReplacement[] = [];
  el.querySelectorAll('video').forEach((video) => {
    const w = video.videoWidth || video.clientWidth || 480;
    const h = video.videoHeight || video.clientHeight || 270;
    const img = document.createElement('img');
    img.src = buildVideoPoster(w, h, 'Video');
    img.style.width = video.style.width || '100%';
    img.style.height = video.style.height || '100%';
    img.style.objectFit = 'contain';
    video.parentElement?.replaceChild(img, video);
    replacements.push({ video, img });
  });
  return replacements;
}

/**
 * Restore original <video> elements from their poster <img> replacements.
 */
export function restoreVideos(replacements: VideoReplacement[]): void {
  replacements.forEach(({ video, img }) => {
    img.parentElement?.replaceChild(video, img);
  });
}

/**
 * Capture a presentation card element via html2canvas.
 * Temporarily removes scale transform, replaces videos with posters,
 * captures at 2x resolution, then restores everything.
 */
export async function captureCard(
  el: HTMLElement,
  html2canvas: (el: HTMLElement, opts: any) => Promise<HTMLCanvasElement>,
): Promise<HTMLCanvasElement> {
  const parent = el.parentElement;
  const savedTransform = el.style.transform;
  const savedParentOverflow = parent?.style.overflow || '';
  const savedParentHeight = parent?.style.height || '';
  // eslint-disable-next-line no-param-reassign
  el.style.transform = 'none';
  if (parent) { parent.style.overflow = 'visible'; parent.style.height = 'auto'; }

  const videoReplacements = replaceVideosWithFrames(el);

  const canvas = await html2canvas(el, { useCORS: true, scale: 2, backgroundColor: '#f5f5f5' });

  restoreVideos(videoReplacements);
  // eslint-disable-next-line no-param-reassign
  el.style.transform = savedTransform;
  if (parent) { parent.style.overflow = savedParentOverflow; parent.style.height = savedParentHeight; }
  return canvas;
}
